import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, Or, Rule, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example {@link AccountGroupCreatedService}
 */
@Injectable({ providedIn: 'root' })
export class RetailPaymentService implements CommonDisplayActionService, CommonDisplayActivityService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private accountGroupService: AccountGroupService,
  ) {}

  getTemplateActionSubtitle(namespace: string, step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    return this.getActionSubtitle(namespace, step, isWaitUntil);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface, isWaitUntil?: boolean): Observable<string> {
    if (step?.choices?.rules?.[0]?.value) {
      const rule = Rule.fromRuleInterface(step.choices.rules[0]) as Or;
      const firstValue = rule.value[0].value;
      const secondValue = rule.value[1].value;
      let status = '';
      if (firstValue && secondValue) {
        status = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.SUCCEEDS').toLowerCase();
      } else if (!firstValue && !secondValue) {
        status = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.FAILS').toLowerCase();
      } else {
        status = this.translateService
          .instant('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.SUCCEEDS_OR_FAILS')
          .toLowerCase();
      }
      let textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.WHEN_ACTION';
      if (isWaitUntil) {
        textKey = 'AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.WAIT_UNTIL_ACTION';
      }
      return this.translateService.stream(textKey, { statusName: status });
    }
    return of(this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.RETAIL_PAYMENT'));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getActionSubtitleSuccessState(data: any): Observable<string> {
    return of('');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getActionSubtitleErrorState(data: any, step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return of('');
  }

  getActivitySubtitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const triggerData = activity?.triggerData ? JSON.parse(activity.triggerData) : {};
    const success = JSON.parse(triggerData.retail_payment_successful || false);
    let status = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.FAILED').toLowerCase();
    if (success) {
      status = this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.SUCCESSFUL').toLowerCase();
    }
    const originalAccountGroupId = triggerData.original_account_group_id;
    if (originalAccountGroupId) {
      return this.accountGroupService.get(originalAccountGroupId, new ProjectionFilter({ napData: true })).pipe(
        take(1),
        map((accountGroup) =>
          this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.ACTIVITY_INTERNAL', {
            customerName: accountGroup.napData.companyName,
            statusName: status,
          }),
        ),
        catchError(() => {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.ACTIVITY', {
            statusName: status,
          });
        }),
      );
    }
    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.RETAIL_PAYMENT.ACTIVITY', {
      statusName: status,
    });
  }

  getActivityTitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TRIGGERS.ENTERED_AUTOMATION');
  }
}
